/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import React, { useCallback, useEffect } from 'react';
import { addDays } from 'date-fns';
import { Checkbox } from 'carbon-react/lib/components/checkbox';

import { FormattedDate, FormattedMessage, useIntl } from '@futrli/localisation';
import { UICarbonNumberField, UIFeatherIcon } from '@futrli/ui-components';

import { checkIfIsInPast } from '../../utils';
import { forceLocalDate } from '../../../../utils/dates';
import { FormulaError, FreestyleMethodState, PredictionHeaderProps } from '../../types';
import { LAST_DAY_OF_MONTH } from '../../../../core/constants';
import { QuickActions } from './quick-actions/quick-actions';
import { StoreOnThe } from './store-on-the/store-on-the';
import { usePredictionsInfoContext } from '../../../../forecast/detail/context/predictions-info-context';
import { PreviewAssumptionImpactsResponse } from '@futrli-sdk/predict';

import styles from './freestyle-content.scss';

interface Props {
  PredictionHeader: React.FunctionComponent<PredictionHeaderProps>;
  currency?: string | null;
  freestyleContent?: FreestyleMethodState;
  onAddFinancialYear: () => void;
  onChange: (freestyleContent: FreestyleMethodState) => void;
  storeOnThe: string;
  handleStoreOnTheChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  previewImpacts: PreviewAssumptionImpactsResponse | undefined;
}

export const FreestyleContent = ({
  PredictionHeader,
  currency,
  freestyleContent,
  onAddFinancialYear,
  onChange,
  storeOnThe,
  handleStoreOnTheChange,
  previewImpacts
}: Props) => {
  const intl = useIntl();
  useEffect(() => {
    if (freestyleContent) {
      onChange(freestyleContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { forecast } = usePredictionsInfoContext();

  const handleFreestyleSelectContentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, year: string, indexToUpdate: number) => {
      if (freestyleContent) {
        onChange({
          ...freestyleContent,
          [year]: freestyleContent[year].map((content, index) => {
            if (indexToUpdate === index) {
              return {
                ...content,
                selected: e.target.checked
              };
            }
            return { ...content };
          })
        });
      }
    },
    [freestyleContent, onChange]
  );

  const handleSelectAllByYear = useCallback(
    (year: string, isSelectAll: boolean) => {
      if (freestyleContent) {
        onChange({
          ...freestyleContent,
          [year]: freestyleContent[year].map(content => {
            return { ...content, selected: isSelectAll ? false : true };
          })
        });
      }
    },
    [freestyleContent, onChange]
  );

  const handleFreestyleAmountContentChange = useCallback(
    (value: string, year: string, indexToUpdate: number) => {
      if (freestyleContent) {
        onChange({
          ...freestyleContent,
          [year]: freestyleContent[year].map((content, index) => {
            if (indexToUpdate === index) {
              return {
                ...content,
                value
              };
            }
            return { ...content };
          })
        });
      }
    },
    [freestyleContent, onChange]
  );

  if (!freestyleContent) {
    return null;
  }

  return (
    <div>
      <PredictionHeader wrap>
        <StoreOnThe onChange={handleStoreOnTheChange} value={storeOnThe} />
        <QuickActions currency={currency} freestyleContent={freestyleContent} onChange={onChange} />
      </PredictionHeader>

      <div className={styles.container}>
        {Object.keys(freestyleContent)
          .map(yearStr => Number(yearStr))
          .sort((a, b) => a - b)
          .map(yearNum => {
            const isSelectAll =
              freestyleContent[yearNum].filter(content => {
                return content.selected;
              }).length === freestyleContent[yearNum].length;
            return (
              <div className={styles.financialYearContainer} key={yearNum}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
                  <Checkbox
                    aria-label={intl.formatMessage(
                      {
                        id: `predict.select_year`,
                        defaultMessage: `Select year {year}`
                      },
                      { year: yearNum }
                    )}
                    checked={isSelectAll}
                    onChange={() => handleSelectAllByYear(String(yearNum), isSelectAll)}
                    mb={0}
                  />
                  <div className={styles.selectYearLabel}>
                    <FormattedMessage
                      id="predict.select_all_year_value"
                      defaultMessage="Select all {currentYear}"
                      values={{ currentYear: yearNum }}
                    />
                  </div>
                </div>
                <div className={styles.financialYear}>
                  {freestyleContent[String(yearNum)].map((data, index) => {
                    const localDate = forceLocalDate(data.date);
                    const isLastDayOfMonth = storeOnThe === LAST_DAY_OF_MONTH;
                    const updatedLocalDate = isLastDayOfMonth
                      ? new Date(localDate.getFullYear(), localDate.getMonth() + 1, 0)
                      : addDays(localDate, Number(storeOnThe) - 1);
                    const isInPast =
                      checkIfIsInPast(updatedLocalDate, isLastDayOfMonth, forecast?.forecast_from_date) &&
                      forecast?.kind !== 'budget';
                    return (
                      <div
                        key={`${yearNum}-${data.date}`}
                        className={styles.carbonItemContainer}
                      >
                        <div className={styles.checkboxContainerCarbon}>
                          <Checkbox
                            aria-label={`Select date ${yearNum}`}
                            tabIndex={-1}
                            style={{ position: 'absolute' }}
                            checked={data.selected}
                            onChange={e => handleFreestyleSelectContentChange(e, String(yearNum), index)}
                            mb={0}
                          />
                          <div className={styles.dateLabel}>
                            <FormattedDate value={localDate} month={'short'} year="2-digit" />
                          </div>
                        </div>
                        <UICarbonNumberField
                          autoComplete="off"
                          type="decimal"
                          value={data.value}
                          onChange={value => handleFreestyleAmountContentChange(value, String(yearNum), index)}
                          placeholder="-"
                          className={isInPast ? styles.inPastInputCarbon : undefined}
                          size="large"
                          mb={0}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        {forecast?.kind === 'budget' ? null : (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div className={styles.iconContainer} onClick={onAddFinancialYear}>
              <UIFeatherIcon iconName="plus" />
            </div>
            <FormattedMessage id="predict.add_financial_year" defaultMessage="Add financial year" />
          </div>
        )}
        {!previewImpacts?.is_valid && previewImpacts?.error_code === FormulaError.ERROR_VALUES_OUT_OF_EXPECTED_RANGE && (
          <div className={styles.formulaError}>
            <FormattedMessage
              id="predict.predict_error_values_out_of_expected_range"
              defaultMessage="Prediction cannot be saved, as it produces values out of expected range."
            />
          </div>
        )}
      </div>
    </div>
  );
};
